import React, { useEffect, useState } from 'react';
import { helpElement } from '../../help/attrs';
import CostingBasisNote from './CostingBasisNote';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import SearchableSelect from '../common/SearchableSelect';
import { StockPeriodReportService } from '../../services/stockPeriodReport.service';
import { StockBucket, StockPeriodFilter, StockPeriodReport as Report, StockPeriodRow } from '../../interfaces/inventory/stockPeriod.interface';
import { getStoresByInstitutionId } from '../../services/store.service';
import { ItemCategoryService } from '../../services/item-category.service';
import { fetchAllPages } from '../../services/salesOrder.service';
import { categoryLabel, sortCategoryTree } from '../../utils/itemCategoryTree';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { saveBlob } from '../../utils/saveBlob';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';

interface Opt { id: string; name: string; }

const lastMonthStart = () => { const d = new Date(); return toLocalIsoDate(new Date(d.getFullYear(), d.getMonth() - 1, 1)); };
const lastMonthEnd = () => { const d = new Date(); return toLocalIsoDate(new Date(d.getFullYear(), d.getMonth(), 0)); };

const thisMonthStart = () => { const d = new Date(); return toLocalIsoDate(new Date(d.getFullYear(), d.getMonth(), 1)); };
const today = () => toLocalIsoDate(new Date());

const Cell: React.FC<{ b: StockBucket }> = ({ b }) => (
  <TableCell align="right">{b.quantity === 0 && Number(b.value) === 0 ? '-' : `${b.quantity} / ${formatMoney(b.value)}`}</TableCell>
);

const COLUMNS: { label: string; helpKey: string; pick: (r: StockPeriodRow) => StockBucket }[] = [
  { label: 'Opening', helpKey: 'store.period.col.opening', pick: r => r.opening }, { label: 'Purchases', helpKey: 'store.period.col.purchases', pick: r => r.purchase },
  { label: 'Landed cost', helpKey: 'store.period.col.landedCost', pick: r => r.landedCost }, { label: 'Customer returns', helpKey: 'store.period.col.customerReturns', pick: r => r.saleReturn },
  { label: 'Transfers in', helpKey: 'store.period.col.transfersIn', pick: r => r.transferIn }, { label: 'Adjustments in', helpKey: 'store.period.col.adjustmentsIn', pick: r => r.adjustmentIn },
  { label: 'Consumption', helpKey: 'store.period.col.consumption', pick: r => r.consumption }, { label: 'Sales', helpKey: 'store.period.col.sales', pick: r => r.saleDelivery },
  { label: 'Transfers out', helpKey: 'store.period.col.transfersOut', pick: r => r.transferOut }, { label: 'Adjustments out', helpKey: 'store.period.col.adjustmentsOut', pick: r => r.adjustmentOut },
  { label: 'Closing', helpKey: 'store.period.col.closing', pick: r => r.closing },
];

/** Stock period valuation: opening, movements by category, closing per store x item, tied to the ledger. */
const StockPeriodReport: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution();
  const [from, setFrom] = useState(lastMonthStart());
  const [to, setTo] = useState(lastMonthEnd());
  const [storeId, setStoreId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [stores, setStores] = useState<Opt[]>([]);
  const [categories, setCategories] = useState<Opt[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  // The filters of the last RUN: downloads must export what is on screen, not whatever was typed since.
  const [applied, setApplied] = useState<StockPeriodFilter | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          fetchAllPages((p, size) => getStoresByInstitutionId(p, size, 'ALL')),
          fetchAllPages((p, size) => ItemCategoryService.getItemCategoriesByInstitution(p, size)),
        ]);
        setStores(s.map(x => ({ id: x.id!, name: x.storeName })));
        // a main category also takes in its sub-categories' items
        setCategories(sortCategoryTree(c).map(x => ({ id: x.id!, name: categoryLabel(x) })));
      } catch (e) { toast.showApiError(e, 'Failed to load the store and category filters'); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filter = () => ({ from, to, storeId: storeId || undefined, categoryId: categoryId || undefined });

  const run = async () => {
    try {
      setLoading(true);
      const f = filter();
      setReport(await StockPeriodReportService.report(institutionId!, f));
      setApplied(f);
    } catch (e) { toast.showApiError(e, 'Failed to load the stock period report'); } finally { setLoading(false); }
  };
  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (kind: 'xlsx' | 'pdf') => {
    if (!applied) return;
    try {
      const blob = kind === 'xlsx' ? await StockPeriodReportService.xlsx(institutionId!, applied) : await StockPeriodReportService.pdf(institutionId!, applied);
      saveBlob(blob, `stock-period-${applied.from}-${applied.to}.${kind}`);
    } catch (e) { toast.showApiError(e, `Failed to download the ${kind.toUpperCase()}`); }
  };

  const check = report?.check;

  return (
    <Box sx={{ p: 3 }} data-help-page="store.period.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Stock Period Report</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="store.period.filter">
        <TextField size="small" data-help="store.period.filter.from" type="date" label="From" value={from} InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 160px', maxWidth: 220 }} onChange={e => setFrom(e.target.value)} />
        <TextField size="small" data-help="store.period.filter.to" type="date" label="To" value={to} InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 160px', maxWidth: 220 }} onChange={e => setTo(e.target.value)} />
        <Button data-help="store.period.thisMonth" size="small" onClick={() => { setFrom(thisMonthStart()); setTo(today()); }}>This month</Button>
        <Button data-help="store.period.lastMonth" size="small" onClick={() => { setFrom(lastMonthStart()); setTo(lastMonthEnd()); }}>Last month</Button>
        <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
          <SearchableSelect<Opt> data-help="store.period.filter.store" options={stores} value={storeId} onChange={id => setStoreId(id)} getOptionLabel={o => o.name}
            label="Store" size="small" placeholder="All stores" />
        </Box>
        <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
          <SearchableSelect<Opt> data-help="store.period.filter.category" options={categories} value={categoryId} onChange={id => setCategoryId(id)} getOptionLabel={o => o.name}
            label="Category" size="small" placeholder="All categories" />
        </Box>
        <Button data-help="store.period.run" variant="contained" onClick={run} disabled={loading}>Run</Button>
        <Button data-help="store.period.excel" startIcon={<FileDownloadIcon />} disabled={!report || !applied} onClick={() => download('xlsx')}>Excel</Button>
        <Button data-help="store.period.pdf" startIcon={<FileDownloadIcon />} disabled={!report || !applied} onClick={() => download('pdf')}>PDF</Button>
      </Box>
      {loading && <CircularProgress />}
      {report && !loading && (
        <>
          <CostingBasisNote method={report.lotCostingMethod} since={report.lotCostingSince} />
          {report.unvaluedRowCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>{report.unvaluedRowCount} row(s) carry quantities with no value; their value columns are understated.</Alert>
          )}
          {check && check.available && (
            <Alert severity={!check.comparable ? 'info' : check.inBalance ? 'success' : 'error'} sx={{ mb: 2 }} data-testid="tie-out-check">
              {check.comparable
                ? `Ledger check: report closing ${formatMoney(check.reportClosingValue)} vs ledger ${formatMoney(check.glBalance)}, difference ${formatMoney(check.difference)}`
                : (check.note ?? 'The ledger check applies to the unfiltered report only.')}
            </Alert>
          )}
          <Paper>
            <TableContainer data-help-section="store.period.table">
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell data-help="store.period.col.store">Store</TableCell><TableCell data-help="store.period.col.item">Item</TableCell>
                  {COLUMNS.map(c => <TableCell key={c.label} align="right" {...helpElement(c.helpKey)}>{c.label}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {report.rows.length === 0 && <TableRow><TableCell colSpan={COLUMNS.length + 2}>No stock movement in this period.</TableCell></TableRow>}
                  {report.rows.map(r => (
                    <TableRow key={`${r.storeId}-${r.itemId}`}>
                      <TableCell>{r.storeName}</TableCell>
                      <TableCell>{r.itemCode} - {r.itemName} {r.unvalued && <Chip size="small" color="warning" label="Unvalued" />}</TableCell>
                      {COLUMNS.map(c => <Cell key={c.label} b={c.pick(r)} />)}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2}><b>Total</b></TableCell>
                    {COLUMNS.map(c => <Cell key={c.label} b={c.pick(report.totals)} />)}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default StockPeriodReport;
